"use client";

/**
 * /alerts/[id] — alert detail page.
 *
 * Access model:
 *   - Anyone (signed-in or not) can VIEW an alert page. The canvas is the
 *     shareable surface. Non-owners see a read-only view: no chat input,
 *     no edit pencils, no delete button.
 *   - Only the OWNER can chat, edit cells, upload alerts, or delete.
 *
 * Implementation:
 *   - GET /alerts/{id} now allows optional auth. Response includes
 *     `is_owner: bool`. We use that to gate all edit affordances.
 *   - Session fetch uses the alert's linked_session_id regardless of who
 *     is viewing; backend serves the messages read-only for non-owners.
 *
 * On delete or 404 (the alert literally doesn't exist), we hard-redirect
 * to xynth.com. 403 is NOT possible anymore since we allow public view.
 */
export const dynamic = "force-dynamic";

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "next/navigation";
import useSWR from "swr";
import { motion } from "framer-motion";
import { Bell, Loader2, Trash2, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  apiClient,
  sessionFetcher,
  type AlertDoc,
  type AlertDetailResponse,
  type PublicProfile,
} from "@/lib/api-client";
import { convertToUIMessages } from "@/lib/session-utils";
import { Chat } from "@/components/chat";
import { ICY, RADIUS, TITLE_CLASS } from "@/lib/alert-theme";
import { useArtifact } from "@/hooks/use-artifact";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { createChannelLogger } from "@/lib/debug-log";
import {
  AlertPublicViewProvider,
  useAlertPublicView,
} from "@/components/alert-page/AlertPaywallContext";
// AlertPageBuddyTour replaces the old OnboardingAlertScene — same trigger
// (`?onboarding=1`) but rendered via the unified BuddyOverlay so the
// visual language matches the replay tour the viewer just came from.
// The old scene-2 component is left in the tree (components/onboarding-alert-scene.tsx)
// for reference but is no longer mounted.
import { AlertPageBuddyTour } from "@/components/buddy/AlertPageBuddyTour";
import { isAutomationsAlertTourVisit } from "@/lib/onboarding-constants";

// Timing channel for alert page load diagnosis. Writes to
// frontend_alert_timing.txt. Safe to keep — the logger no-ops off-localhost.
const timingLog = createChannelLogger("alert_timing");

// Dedicated channel for the main-chat → alert-page edit_alert handoff flow.
// Writes to frontend_edit_alert_handoff.txt. Traces the `?q=` auto-submit
// chain end-to-end so we can pinpoint exactly where it breaks (gate,
// append effect, URL strip). Safe to keep — logger no-ops off-localhost.
const handoffLog = createChannelLogger("edit_alert_handoff");

// Time since navigation start (what the user perceives as "when I clicked").
// Falls back to performance.now() if navigation timing isn't available.
function tSinceNav(): number {
  if (typeof performance === "undefined") return -1;
  const navs = performance.getEntriesByType?.("navigation") as PerformanceNavigationTiming[] | undefined;
  const navStart = navs?.[0]?.startTime ?? 0;
  return Math.round(performance.now() - navStart);
}

export default function AlertDetailPage() {
  const params = useParams();
  const searchParams = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  const alertId = params?.id as string;

  // Optional ?q=<query> auto-submit handoff from the main chat's edit_alert
  // tool. When the main-chat agent calls edit_alert, its tool display opens
  // /alerts/{alert_id}?q=<url-encoded query> in a new tab; this page reads
  // that query below and passes it to <Chat> as initialUserQuery so the
  // alert-linked agent auto-receives it as the user's first message. We also
  // gate it to OWNERS ONLY (see `initialUserQuery` below) so randoms can't
  // inject a query via a shared link.
  const initialQueryFromUrl = searchParams?.get("q") ?? undefined;

  // Log the raw ?q= param we saw on mount. This is hop #2 of the edit_alert
  // handoff (hop #1 is the tool display card calling window.open). We log
  // presence + length + first 80 chars so we can tell in frontend_edit_alert_handoff.txt
  // whether the URL param actually survived the hop from the main-chat tab.
  handoffLog("page:raw_q_param", {
    t: tSinceNav(),
    alertId,
    userId: user?.uid || "anon",
    hasQ: Boolean(initialQueryFromUrl),
    qLen: initialQueryFromUrl?.length ?? 0,
    qPreview: initialQueryFromUrl ? initialQueryFromUrl.slice(0, 80) : null,
  });

  // Log component first render (module eval → first function call).
  timingLog("page:mount", { t: tSinceNav(), alertId, userId: user?.uid || "anon" });

  // SWR key includes user so a sign-in/sign-out re-fetches. The key is the
  // alertId scoped by "anon" for signed-out visitors, so everyone gets a
  // cacheable, public fetch.
  const alertSwrKey = useMemo(
    () =>
      alertId && !authLoading
        ? `alert-${alertId}-${user?.uid || "anon"}`
        : null,
    [alertId, authLoading, user?.uid],
  );

  const {
    data: alertData,
    error: alertError,
    isLoading: alertLoading,
    mutate: mutateAlert,
  } = useSWR<AlertDetailResponse>(
    alertSwrKey,
    async () => {
      const startT = tSinceNav();
      timingLog("fetch:alert:start", { t: startT });
      try {
        const result = await apiClient.getAlert(alertId);
        timingLog("fetch:alert:done", { t: tSinceNav(), duration_ms: tSinceNav() - startT });
        return result;
      } catch (err) {
        timingLog("fetch:alert:error", { t: tSinceNav(), error: String(err) });
        throw err;
      }
    },
    {
      refreshInterval: 10_000,
      revalidateOnFocus: true,
      dedupingInterval: 1_500,
      shouldRetryOnError: false,
    },
  );

  const alert = alertData?.alert;
  const forcePublicTourView = isAutomationsAlertTourVisit(searchParams);
  const isOwner = Boolean(alertData?.is_owner) && !forcePublicTourView;
  const isSubscribed = Boolean(alertData?.is_subscribed);
  const subscribedEmail = alertData?.subscribed_email ?? null;
  // Owner-only privacy field — undefined for non-owners (backend omits).
  // Propagate so the info popover can surface "N subscribers".
  const subscriberCount = alertData?.subscriber_count;
  // Public byline (creator or anonymous). Backend always populates this;
  // the frontend only renders it on the non-owner view (see AlertTopBar).
  const presentedBy = alertData?.presented_by ?? null;
  const linkedSessionId = alert?.linked_session_id;

  // 410 Gone = alert was deleted by owner. The backend sends
  // `{status: "deleted", deleted_at, name}` so we can render a friendly
  // landing instead of a generic 404. We detect via a dedicated state
  // below (see `isAlertDeleted`).
  const isAlertDeleted = useMemo(() => {
    if (!alertError) return false;
    const msg = String(alertError?.message || "");
    return msg.includes(" 410");
  }, [alertError]);

  // Session fetch: only for OWNERS. Non-owner visitors get their own
  // sandbox session (id `alert-visit-{alertId}-{uid}`) and never read
  // the owner's linked-session messages. We hard-skip the fetch for
  // them so we never accidentally show owner build history under the
  // visitor's chat — that would be both a UX and a privacy bug.
  //
  // Aggressive revalidation: the alert page is inherently multi-tab — a
  // user might be editing an alert in tab A, open the same alert in
  // tab B, and expect tab B to pick up the latest turn they did in A.
  // SWR defaults to revalidateOnFocus=true but with a dedupingInterval
  // of 2s. We also tighten that so tab-switches always fetch fresh, and
  // we do a 15s background poll so even a forgotten tab eventually syncs.
  const {
    data: sessionData,
    error: sessionError,
    isLoading: sessionLoading,
  } = useSWR(
    linkedSessionId && isOwner
      ? `session-${linkedSessionId}-${user?.uid || "anon"}`
      : null,
    async () => {
      const startT = tSinceNav();
      timingLog("fetch:session:start", { t: startT, linkedSessionId });
      // Also log to the handoff channel so we see these events in one file
      // alongside page:raw_q_param / page:gate / chat:append. This captures
      // mid-flight revalidations that may clobber the chat's messages array
      // after the user's optimistic message is appended.
      handoffLog("page:session:fetch:start", {
        t: startT,
        linkedSessionId,
      });
      try {
        const result = await sessionFetcher(linkedSessionId!, user?.uid);
        timingLog("fetch:session:done", {
          t: tSinceNav(),
          duration_ms: tSinceNav() - startT,
          messageCount: result?.messages?.length ?? 0,
        });
        handoffLog("page:session:fetch:done", {
          t: tSinceNav(),
          duration_ms: tSinceNav() - startT,
          linkedSessionId,
          messageCount: result?.messages?.length ?? 0,
        });
        return result;
      } catch (err) {
        timingLog("fetch:session:error", { t: tSinceNav(), error: String(err) });
        handoffLog("page:session:fetch:error", {
          t: tSinceNav(),
          error: String(err),
        });
        throw err;
      }
    },
    {
      // We fetch the session ONCE to hydrate <Chat>'s initialMessages.
      // After the first successful fetch, <Chat>'s useChat hook owns the
      // live message state — any SWR revalidation here would overwrite
      // the optimistic user append and cause the "message flashes then
      // disappears" UX bug. Multi-tab sync for messages is not worth
      // that cost; if the user needs fresh state they can reload.
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 0,
      revalidateIfStale: false,
      revalidateOnMount: true,
      dedupingInterval: 60_000,
      shouldRetryOnError: false,
    },
  );

  // 404 = alert ID was never valid → redirect to marketing.
  // 410 (deleted) is handled in-page by `isAlertDeleted` render branch —
  // we no longer redirect on that because the user deserves to know their
  // shared link was taken down, not just disappear into xynth.com.
  useEffect(() => {
    if (alertError) {
      const msg = String(alertError?.message || "");
      if (msg.includes(" 404") && !msg.includes(" 410")) {
        console.info("[alert-page] alert not found (404) → https://xynth.com");
        window.location.href = "https://xynth.com";
      }
    }
  }, [alertError]);

  // ── Handoff observability: detect SWR sessionData changes/re-fetches ──
  // If SWR re-fetches /session while the user's message is still in-flight,
  // the new sessionData.messages array lands → <Chat>'s initialMessages
  // prop changes → useChat may reconcile and briefly drop the optimistic
  // user message. This log tracks every sessionData change so we can
  // correlate with `chat:messages:changed` on the same timeline.
  const sessionDataRefForLog = useRef<typeof sessionData | null>(null);
  const sessionDataChangeCountRef = useRef<number>(0);
  useEffect(() => {
    if (!sessionData) return;
    const prev = sessionDataRefForLog.current;
    const prevLen = prev?.messages?.length ?? -1;
    const newLen = sessionData.messages?.length ?? -1;
    const sameRef = prev === sessionData;
    sessionDataRefForLog.current = sessionData;
    sessionDataChangeCountRef.current += 1;
    handoffLog("page:session:data:changed", {
      t: tSinceNav(),
      changeCount: sessionDataChangeCountRef.current,
      sameRef,
      prevMessageCount: prevLen,
      newMessageCount: newLen,
      lenDelta: newLen - prevLen,
    });
  }, [sessionData]);

  // ── FROZEN initialMessages snapshot (fix for SWR-clobbers-optimistic-append) ──
  // Problem we are solving: when SWR re-fetches /session mid-flight (e.g.
  // while the user's just-appended message is still streaming), the new
  // `sessionData.messages` array lands, we recompute `messages` via
  // `convertToUIMessages` every render, and pass it to <Chat> as
  // `initialMessages`. The AI SDK's useChat sees a *new initialMessages
  // reference* and reconciles its internal messages array to match — which
  // wipes the user's optimistic append. The UX symptom is: user's message
  // flashes in → vanishes → reappears when the agent's streamed response
  // comes back from the server with the persisted history.
  //
  // Fix: snapshot the messages array ONCE (first time sessionData arrives)
  // and never update it after. useChat owns the live message state from
  // that point onward. SWR revalidations still update `sessionData` in
  // this component for *other* consumers (e.g. per-alert metadata), but
  // we do NOT feed them back into <Chat>'s initialMessages.
  //
  // Remount escape hatch: we key <Chat> by `sessionData.session_id` (see
  // below, unchanged). If the session_id genuinely changes we tear down
  // and remount — which is correct, that's a different conversation.
  const frozenInitialMessagesRef = useRef<
    ReturnType<typeof convertToUIMessages> | null
  >(null);
  const frozenLoggedRef = useRef(false);
  if (
    frozenInitialMessagesRef.current === null &&
    sessionData?.messages !== undefined
  ) {
    frozenInitialMessagesRef.current = convertToUIMessages(
      sessionData.messages || [],
    );
    // Defer logging to avoid logging mid-render; handled by effect below.
  }
  useEffect(() => {
    if (frozenInitialMessagesRef.current && !frozenLoggedRef.current) {
      frozenLoggedRef.current = true;
      handoffLog("page:initial_messages:frozen", {
        t: tSinceNav(),
        frozenCount: frozenInitialMessagesRef.current.length,
      });
    }
  });

  // ─── Pre-seed the artifact store BEFORE <Chat> mounts ──────────────────
  // Why this exists: <Chat> renders two completely different branches
  // depending on artifact.isVisible (see chat.tsx ~line 1462). When
  // isVisible=false (SWR fallback default), <Chat> renders the plain
  // chat-shell (ChatHeader + messages + input) — which on /alerts/[id]
  // briefly flashes the side chat before <Chat>'s own useLayoutEffect flips
  // the store to alert mode. That's the ~1s chat flash.
  //
  // Fix: seed the store HERE, at the page level, synchronously before we
  // ever render <Chat>. Because SWR's mutate writes the cache immediately,
  // <Chat>'s very first render already sees isVisible=true + chat minimized
  // and goes straight to the <Artifact> canvas-full branch. No flash.
  //
  // We gate on `alert` being present so this only runs once data is ready
  // (the loading states below still return <PageLoading> on first paint).
  // useLayoutEffect ensures this commits before any paint of the page's
  // real content. Mobile is skipped — it uses its own full-screen toggle.
  const { setArtifact } = useArtifact();
  const isMobile = useIsMobile();

  // Render-time log: captures isMobile value seen on every page render.
  // Useful because `useIsMobile()` can flip after hydration — a false log
  // on render #1 followed by a true log on render #2 means the seed ran
  // under the wrong assumption.
  timingLog("page:render", {
    t: tSinceNav(),
    isMobile,
    hasAlert: !!alert,
    alertId: alert?._id ?? null,
  });

  // One-shot guard for the artifact seed. Prevents the effect from re-seeding
  // the store (and slamming the chat panel back to minimized) on every later
  // re-render. Particularly important for the edit_alert handoff: after the
  // ?q= is consumed by chat.tsx it strips the URL, which flips
  // `initialQueryFromUrl` to undefined on the next render. Without this ref
  // the effect would re-run with isHandoff=false and minimize the chat panel
  // the user just saw auto-open.
  const hasSeededArtifactRef = useRef(false);

  useLayoutEffect(() => {
    if (!alert) {
      timingLog("seed:skip", { t: tSinceNav(), reason: "no_alert", isMobile });
      return;
    }
    if (hasSeededArtifactRef.current) {
      timingLog("seed:skip", { t: tSinceNav(), reason: "already_seeded", isMobile });
      return;
    }
    // Mobile gets the SAME canvas-first seed as desktop. Previously we
    // skipped mobile here, which left the store at isVisible=false and
    // caused <Chat> to render its `chat_shell` branch — so mobile users
    // landed on the full chat instead of the alert canvas. Logs (see
    // xynth-frontend/frontend_alert_timing.txt) proved the skip was the
    // root cause: "seed:skip reason=is_mobile" → "chat:branch=chat_shell".
    //
    // Edit-alert handoff tweak: when the page is loaded with a ?q= param
    // (main-chat agent called edit_alert and the user clicked "Open editor"),
    // we need the chat panel OPEN on mount so the user can see the alert
    // agent's streaming response to their auto-submitted query. Without
    // this, the canvas renders full-screen, the agent runs in a minimized
    // chat the user can't see, and it looks like "nothing happened" even
    // though the model is busy replying. We only flip isChatMinimized=false
    // in that specific case — for a normal alert page visit (no ?q=) the
    // canvas-first default is preserved. See handoffLog("page:gate:eval")
    // for the corresponding gate trace.
    //
    // ONE-SHOT: we seed exactly once per mount. After this runs, the user
    // can freely toggle the chat panel via the UI; we never clobber their
    // choice just because the URL changed or another re-render happened.
    const isHandoff = Boolean(initialQueryFromUrl);
    hasSeededArtifactRef.current = true;
    timingLog("seed:fired", { t: tSinceNav(), isMobile, isHandoff });
    handoffLog("page:seed:fired", {
      t: tSinceNav(),
      isHandoff,
      chatMinimized: !isHandoff,
      oneShot: true,
    });
    setArtifact((prev) => ({
      ...prev,
      isVisible: true,
      documentId: alert.linked_session_id || alert._id,
      isChatMinimized: !isHandoff,
      isArtifactMinimized: false,
    }));
  }, [alert, isMobile, setArtifact, initialQueryFromUrl]);

  const handleAlertMutated = useCallback(() => {
    mutateAlert();
  }, [mutateAlert]);

  const handleAlertDeleted = useCallback(() => {
    console.info("[alert-page] redirect after delete → https://xynth.com");
    window.location.href = "https://xynth.com";
  }, []);

  if (alertLoading && !alert) {
    timingLog("render:branch", { t: tSinceNav(), branch: "page_loading_alert" });
    return <PageLoading label="Loading alert…" />;
  }

  // 410 Gone — alert was deleted by owner. Show a dedicated landing
  // instead of the generic error card so the visitor knows it existed
  // and knows where to go from here. We don't reveal the alert name
  // from the response body here — the backend does send it, but a
  // stranger hitting a dead share-link doesn't need to be told what
  // the alert was called; keeps it clean.
  if (isAlertDeleted) {
    timingLog("render:branch", { t: tSinceNav(), branch: "alert_deleted_landing" });
    return <AlertDeletedLanding />;
  }

  if (alertError && !alert) {
    return (
      <div className="relative h-full w-full flex items-center justify-center bg-background">
        <div
          className={`${RADIUS.card} border ${ICY.ring} ${ICY.surface} px-6 py-5 max-w-[420px] flex flex-col gap-2 text-center`}
        >
          <p className={TITLE_CLASS}>Couldn&apos;t load this alert</p>
          <p className="text-xs text-slate-300/85 leading-relaxed">
            {String(alertError?.message || "Unknown error")}
          </p>
          <button
            onClick={() => mutateAlert()}
            className="mt-2 text-[12px] text-sky-200 hover:text-sky-100 font-medium"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!alert) {
    timingLog("render:branch", { t: tSinceNav(), branch: "page_loading_no_alert" });
    return <PageLoading label="Loading alert…" />;
  }

  if (!linkedSessionId) {
    return (
      <div className="relative h-full w-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-center max-w-[380px] px-6">
          <p className="text-sm text-foreground">Alert canvas not ready</p>
          <p className="text-[12px] text-muted-foreground leading-relaxed">
            This alert was created before linked sessions rolled out.
            {isOwner ? " Try refreshing; the backend auto-heals on load." : ""}
          </p>
          {isOwner && (
            <button
              onClick={() => mutateAlert()}
              className="mt-2 text-[12px] text-sky-200 hover:text-sky-100"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── NON-OWNER VISITOR BRANCH ───────────────────────────────────────
  // Diverges from owner flow BEFORE we fetch the linked session — the
  // visitor never reads owner messages and never streams into the
  // linked session. They get a sandbox session id so refreshes / return
  // visits resume their own past chats with this alert.
  //
  // Why this branches early (before sessionLoading checks):
  //   - We force-skipped the SWR session fetch above when !isOwner,
  //     so sessionData is permanently undefined here and the owner
  //     branch's "Couldn't load the alert session" error card would
  //     fire immediately for every visitor. Branching here avoids that.
  //   - Canvas uses `alert.linked_session_id` + `alert_start_cell_index`
  //     from GET /alerts (not the visitor sandbox session id).
  if (!isOwner) {
    timingLog("render:branch", {
      t: tSinceNav(),
      branch: "visitor_chat",
      hasUser: Boolean(user?.uid),
    });
    return (
      <AlertPublicViewProvider
        alert={alert}
        isOwner={false}
        initialIsSubscribed={isSubscribed}
        defaultEmail={user?.email ?? undefined}
        onMutated={handleAlertMutated}
      >
        {/* Onboarding scene 2 — visitor branch. Reads ?onboarding=1 and
            starts the BuddyOverlay-driven tour over the alert canvas. */}
        <AlertPageBuddyTour alertName={alert.name} />
        <VisitorAlertChat
          alert={alert}
          alertStartCellIndex={alertData?.alert_start_cell_index ?? 0}
          presentedBy={presentedBy}
          isSubscribed={isSubscribed}
          subscribedEmail={subscribedEmail}
          subscriberCount={subscriberCount}
          onAlertMutated={handleAlertMutated}
          onAlertDeleted={handleAlertDeleted}
        />
      </AlertPublicViewProvider>
    );
  }

  // ── OWNER FLOW (below) — needs linked-session data ─────────────────
  if (sessionLoading && !sessionData) {
    timingLog("render:branch", { t: tSinceNav(), branch: "page_loading_session" });
    return <PageLoading label="Loading alert session…" />;
  }

  if ((sessionError && !sessionData) || !sessionData) {
    return (
      <div className="relative h-full w-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-center max-w-[380px] px-6">
          <p className="text-sm text-foreground">
            Couldn&apos;t load the alert session
          </p>
          <p className="text-[12px] text-muted-foreground leading-relaxed">
            {String(sessionError?.message || "Session not found.")}
          </p>
        </div>
      </div>
    );
  }

  const messages =
    frozenInitialMessagesRef.current ??
    convertToUIMessages(sessionData.messages || []);

  // Owner-only auto-submit: the visitor branch returned earlier so by
  // here we know isOwner=true and the session isn't readonly. We still
  // gate on `gateHasQuery` so the no-?q= case skips the auto-submit.
  // Anti-replay on refresh is handled inside chat.tsx via sessionStorage
  // keyed by the ?q= value (so a refresh with ?q= still in the URL does
  // not re-fire the same query).
  const gateHasQuery = Boolean(initialQueryFromUrl);
  handoffLog("page:gate:eval", {
    t: tSinceNav(),
    hasQ: gateHasQuery,
    messageCount: messages.length,
    isOwner: true,
    passed: gateHasQuery,
  });

  const initialUserQuery = gateHasQuery ? initialQueryFromUrl : undefined;

  timingLog("render:branch", { t: tSinceNav(), branch: "chat_mounted", messageCount: messages.length, hasInitialQuery: Boolean(initialUserQuery) });
  handoffLog("page:pass_to_chat", {
    t: tSinceNav(),
    hasInitialQuery: Boolean(initialUserQuery),
    initialQueryLen: initialUserQuery?.length ?? 0,
  });

  // Owner-only chat: streams into the alert's linked session, has full
  // edit/upload/delete tools, and (when ?q= came from edit_alert) auto-
  // submits the handoff query. Non-owners returned earlier from the
  // visitor branch above so we don't need to wrap in the public-view
  // provider here.
  //
  // AlertPageBuddyTour mounts here too because the canonical onboarding
  // alert (alert-7a209a4f9f5d) is owned by the founder account — when the
  // owner walks their own onboarding the page fires this branch. The tour
  // reads ?onboarding=1 and is a no-op otherwise.
  return (
    <AlertPublicViewProvider
      alert={alert}
      isOwner={true}
      initialIsSubscribed={isSubscribed}
      defaultEmail={user?.email ?? undefined}
      onMutated={handleAlertMutated}
    >
      <AlertPageBuddyTour alertName={alert.name} />
      <Chat
        id={sessionData.session_id}
        initialMessages={messages}
        initialVisibilityType={sessionData.visibility || "private"}
        isReadonly={false}
        readonlyReason={undefined}
        autoResume={true}
        section="chat"
        title={alert.name || sessionData.title || "Alert chat"}
        viewMode={(alert as { kind?: string })?.kind === "signal" ? "signal" : "alert"}
        alertStartCellIndex={
          (sessionData as any)?.alert_start_cell_index ?? 0
        }
        alertDoc={alert}
        alertIsOwner={true}
        alertIsSubscribed={isSubscribed}
        alertSubscribedEmail={subscribedEmail}
        alertSubscriberCount={subscriberCount}
        alertPresentedBy={presentedBy}
        isAlertsRoute={true}
        initialUserQuery={initialUserQuery}
        onAlertMutated={handleAlertMutated}
        onAlertDeleted={handleAlertDeleted}
      />
    </AlertPublicViewProvider>
  );
}

/**
 * Visitor chat shell for /alerts/[id] when the viewer is NOT the owner.
 *
 * Why this is its own component:
 *   - It needs to consume `useAlertPublicView()` (the gate hook), which
 *     is only valid INSIDE the provider. Living as a child component
 *     keeps the page-level layout clean.
 *   - It builds its own session id (`alert-visit-{alertId}-{uid}`),
 *     deterministic per-(user, alert), so the visitor's chats with this
 *     alert persist across reloads but are isolated from owner state
 *     and from other visitors' chats.
 *   - It owns the gate function passed to <Chat>'s onVisitorSubmitGate.
 *     The gate fires synchronously on submit:
 *       - signed out → opens AuthModal, blocks submit
 *       - signed in but free tier → opens PlansModal, blocks submit
 *       - signed in + paid → returns true, message streams normally
 *
 * Anonymous (signed-out) viewers still see the input — but the very
 * first keystroke-then-send pops AuthModal. That's intentional UX: the
 * input is the conversion surface (you have to TYPE to feel locked
 * out), matching the pattern used elsewhere in the app.
 */
function VisitorAlertChat({
  alert,
  alertStartCellIndex,
  presentedBy,
  isSubscribed,
  subscribedEmail,
  subscriberCount,
  onAlertMutated,
  onAlertDeleted,
}: {
  alert: AlertDoc;
  /** From `GET /alerts/{id}` — linked session's `alert_start_cell_index` for canvas filtering. */
  alertStartCellIndex: number;
  presentedBy: PublicProfile | null;
  isSubscribed: boolean;
  subscribedEmail: string | null;
  subscriberCount: number | undefined;
  onAlertMutated: () => void;
  onAlertDeleted: () => void;
}) {
  const { user } = useAuth();
  const { canGoLive, openPaywall } = useAlertPublicView();

  // Deterministic visitor session id. Three regimes:
  //   - signed in: stable id `alert-visit-{alertId}-{uid}`. Refreshing
  //     the page or coming back tomorrow resumes the SAME chat.
  //   - signed out: synthetic guest id `alert-visit-{alertId}-anon-...`
  //     — never sent to the backend (gate blocks submit), but useChat
  //     still requires a non-empty id for its internal SWR cache key.
  //
  // Generated once per mount via useMemo so the id is stable across
  // re-renders (otherwise useChat would tear down on every parent
  // re-render — fatal for streaming UX).
  const visitorSessionId = useMemo(() => {
    if (!alert?._id) return "alert-visit-pending";
    if (user?.uid) return `alert-visit-${alert._id}-${user.uid}`;
    // Stable across THIS pageload but unique per mount, so signed-out
    // pre-auth viewers don't share an id with the next anon visitor in
    // the rare case both get persisted server-side.
    return `alert-visit-${alert._id}-anon-${Math.random().toString(36).slice(2, 10)}`;
  }, [alert?._id, user?.uid]);

  // Synchronous gate the floating input calls on submit. Returning
  // `false` cancels the append in <Chat> / <Artifact>'s wrappedAppend.
  // We intentionally route to the existing AlertPublicViewProvider
  // modals (PlansModal + AuthModal) so the visitor sees the SAME
  // paywall/auth UX as the rest of the app — no bespoke modal here.
  const visitorSubmitGate = useCallback((): boolean => {
    if (!user?.uid) {
      handoffLog("visitor:gate:denied", {
        t: tSinceNav(),
        reason: "not_signed_in",
        alertId: alert?._id,
      });
      openPaywall("live");
      return false;
    }
    if (!canGoLive) {
      handoffLog("visitor:gate:denied", {
        t: tSinceNav(),
        reason: "not_paying",
        alertId: alert?._id,
        userId: user?.uid,
      });
      openPaywall("live");
      return false;
    }
    handoffLog("visitor:gate:passed", {
      t: tSinceNav(),
      alertId: alert?._id,
      userId: user?.uid,
    });
    return true;
  }, [user?.uid, canGoLive, openPaywall, alert?._id]);

  return (
    <Chat
      id={visitorSessionId}
      initialMessages={[]}
      initialVisibilityType="private"
      isReadonly={false}
      readonlyReason={undefined}
      // autoResume only makes sense for signed-in visitors — anon
      // visitors never persist server-side, so there's nothing to
      // resume on a refresh. Signed-in visitors get TRUE so a reload
      // re-hydrates their pending stream if any was in-flight.
      autoResume={Boolean(user?.uid)}
      section="chat"
      title={alert.name || "Alert chat"}
      viewMode={(alert as { kind?: string })?.kind === "signal" ? "signal" : "alert"}
      alertStartCellIndex={alertStartCellIndex}
      alertDoc={alert}
      alertIsOwner={false}
      alertIsSubscribed={isSubscribed}
      alertSubscribedEmail={subscribedEmail}
      alertSubscriberCount={subscriberCount}
      alertPresentedBy={presentedBy}
      isAlertsRoute={true}
      onAlertMutated={onAlertMutated}
      onAlertDeleted={onAlertDeleted}
      // Visitor-only props — owner branch leaves these undefined.
      alertVisitorMode={true}
      alertContextId={alert._id}
      onVisitorSubmitGate={visitorSubmitGate}
    />
  );
}

/**
 * Landing page for a dead /alerts/{id} link (owner deleted the alert).
 * Shows a friendly message + a path forward instead of redirecting into
 * the xynth.com void the way we used to — some sharers want to know why
 * their shared link stopped working.
 */
function AlertDeletedLanding() {
  return (
    <div className="relative h-full w-full flex items-center justify-center bg-background px-6">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="max-w-[440px] w-full rounded-2xl border border-border/60 bg-card/70 backdrop-blur-sm px-6 py-7 text-center"
      >
        <div className="w-14 h-14 mx-auto rounded-full bg-slate-800/70 border border-slate-700/70 flex items-center justify-center mb-3">
          <Trash2 className="w-6 h-6 text-slate-400" strokeWidth={1.8} />
        </div>
        <h1 className="text-xl font-semibold text-foreground leading-tight mb-1.5 tracking-tight">
          This alert was removed
        </h1>
        <p className="text-[13px] leading-relaxed text-muted-foreground mb-5">
          The owner deleted this alert, so there&apos;s nothing to show here.
          Check out other alerts on Xynth or build your own.
        </p>
        <div className="flex items-center justify-center gap-2">
          <a
            href="https://xynth.com"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-sky-500 hover:bg-sky-400 text-white text-sm font-semibold transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Explore Xynth
          </a>
        </div>
      </motion.div>
    </div>
  );
}

function PageLoading({ label }: { label: string }) {
  return (
    <div className="relative h-full w-full flex items-center justify-center bg-background">
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="flex flex-col items-center gap-3"
      >
        <div
          className={`relative w-12 h-12 ${RADIUS.chip} ${ICY.iconBg} border ${ICY.ring} flex items-center justify-center`}
        >
          <Bell className={`w-5 h-5 ${ICY.iconFg}`} strokeWidth={1.8} />
          <motion.span
            className={`absolute inset-0 ${RADIUS.chip}`}
            animate={{
              boxShadow: [
                "0 0 0 0 rgba(186,230,253,0.28)",
                "0 0 0 10px rgba(186,230,253,0)",
              ],
            }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
          />
        </div>
        <div className="flex items-center gap-2 text-slate-200">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-200" />
          <span className="text-sm">{label}</span>
        </div>
      </motion.div>
    </div>
  );
}
